/**
 * Маппинг между EventBudgetItem (Events API) и BudgetItem (shared-types / Finance UI).
 * Без as any; явные enum-маппинги; variance = plannedAmount - actualAmount;
 * type через нормализацию строки или сравнение с enum.
 */

import type { EventBudgetItem } from "../types/events-unified";
import type { BudgetItem } from "../types/shared-types";
import {
  BudgetItemType,
  BudgetCategory,
  BudgetStatus,
  BudgetPeriod,
  BudgetPriority,
} from "../types/shared-types";
import { BudgetItemStatus } from "../types/enums/event-enums";

// DTO shape expected by POST /events/:id/budget/items
export interface CreateBudgetItemPayload {
  title: string;
  description?: string;
  type: string;
  category: string;
  status: string;
  plannedAmount: number;
  actualAmount?: number;
}

const EVENT_CATEGORY_TO_BUDGET_CATEGORY: Record<string, BudgetCategory> = {
  ticket_sales: BudgetCategory.OTHER,
  sponsorship: BudgetCategory.OTHER,
  venue_rental: BudgetCategory.FACILITIES,
  venue: BudgetCategory.FACILITIES,
  catering: BudgetCategory.SUPPLIES,
  equipment: BudgetCategory.TECHNOLOGY,
  marketing: BudgetCategory.MARKETING,
  staff: BudgetCategory.PERSONNEL,
  transportation: BudgetCategory.TRAVEL,
  transport: BudgetCategory.TRAVEL,
  insurance: BudgetCategory.OTHER,
  other: BudgetCategory.OTHER,
  materials: BudgetCategory.SUPPLIES,
  accommodation: BudgetCategory.TRAVEL,
  speakers: BudgetCategory.OTHER,
  entertainment: BudgetCategory.OTHER,
  operations: BudgetCategory.OPERATIONS,
  personnel: BudgetCategory.PERSONNEL,
  technology: BudgetCategory.TECHNOLOGY,
  facilities: BudgetCategory.FACILITIES,
  travel: BudgetCategory.TRAVEL,
  supplies: BudgetCategory.SUPPLIES,
};

function mapEventCategoryToBudgetCategory(category: string): BudgetCategory {
  const key = String(category ?? "").toLowerCase();
  return EVENT_CATEGORY_TO_BUDGET_CATEGORY[key] ?? BudgetCategory.OTHER;
}

const EVENT_STATUS_TO_BUDGET_STATUS: Record<string, BudgetStatus> = {
  planned: BudgetStatus.PLANNED,
  approved: BudgetStatus.APPROVED,
  in_progress: BudgetStatus.IN_PROGRESS,
  ordered: BudgetStatus.IN_PROGRESS,
  completed: BudgetStatus.COMPLETED,
  confirmed: BudgetStatus.CONFIRMED,
  paid: BudgetStatus.PAID,
  cancelled: BudgetStatus.CANCELLED,
  over_budget: BudgetStatus.OVER_BUDGET,
};

function mapEventStatusToBudgetStatus(status: string): BudgetStatus {
  const key = String(status ?? "").toLowerCase();
  return EVENT_STATUS_TO_BUDGET_STATUS[key] ?? BudgetStatus.PLANNED;
}

/**
 * EventBudgetItem (Events API) -> BudgetItem (Finance UI).
 * dueDate/paymentDate используются только если есть в типе; иначе new Date().
 * type: нормализация через toLowerCase и сравнение с 'income'.
 */
export function eventBudgetItemToBudgetItem(
  item: EventBudgetItem,
  _options?: { eventId?: string },
): BudgetItem {
  const planned = Number(item.plannedAmount ?? 0);
  const actual = Number(item.actualAmount ?? 0);
  const startDate = item.dueDate != null ? new Date(item.dueDate) : new Date();
  const endDate =
    item.paymentDate != null ? new Date(item.paymentDate) : new Date();
  const typeStr = String(item.type ?? "").toLowerCase();
  const TYPE_MAP: Record<string, BudgetItemType> = {
    income: BudgetItemType.INCOME,
    expense: BudgetItemType.EXPENSE,
    transfer: BudgetItemType.TRANSFER,
    refund: BudgetItemType.REFUND,
  };
  const type: BudgetItemType = TYPE_MAP[typeStr] ?? BudgetItemType.EXPENSE;

  const now = new Date();
  return {
    id: item.id,
    title: item.title ?? item.name ?? "",
    description: item.notes ?? item.description ?? "",
    type,
    category: mapEventCategoryToBudgetCategory(String(item.category)),
    priority: BudgetPriority.MEDIUM,
    status: mapEventStatusToBudgetStatus(String(item.status)),
    plannedAmount: planned,
    actualAmount: actual,
    variance: planned - actual,
    startDate,
    endDate,
    period: BudgetPeriod.MONTHLY,
    year: now.getFullYear(),
    createdAt: item.createdAt != null ? new Date(item.createdAt) : now,
    updatedAt: item.updatedAt != null ? new Date(item.updatedAt) : now,
  };
}

/** Обратное отображение: BudgetCategory (shared) -> строка для Events API (backend enum). */
const BUDGET_CATEGORY_TO_EVENT_CATEGORY: Record<BudgetCategory, string> = {
  [BudgetCategory.MARKETING]: "marketing",
  [BudgetCategory.OPERATIONS]: "other",
  [BudgetCategory.PERSONNEL]: "staff",
  [BudgetCategory.TECHNOLOGY]: "equipment",
  [BudgetCategory.FACILITIES]: "venue_rental",
  [BudgetCategory.TRAVEL]: "transportation",
  [BudgetCategory.SUPPLIES]: "catering",
  [BudgetCategory.OTHER]: "other",
};

/**
 * BudgetStatus (shared) -> строка для Events API.
 *
 * Набор на той стороне закрыт: `@IsEnum(BudgetItemStatus)` принимает ровно
 * planned / approved / in_progress / completed / cancelled. Значения, которых в
 * нём нет, приводятся к ближайшему, а `paid` оставлен как есть намеренно: своего
 * «оплачено» у статьи мероприятия нет, и подменять его на «завершено» значило бы
 * молча переписать смысл. Сегодня эта ячейка недостижима — форма экрана бюджета
 * мероприятия ведёт словарь МЕРОПРИЯТИЯ, где `paid` отсутствует; если путь
 * появится, сервер ответит 400, а не примет ложь (AD-472).
 */
const BUDGET_STATUS_TO_EVENT_STATUS: Record<BudgetStatus, string> = {
  [BudgetStatus.DRAFT]: "planned",
  [BudgetStatus.PLANNED]: "planned",
  [BudgetStatus.APPROVED]: "approved",
  [BudgetStatus.IN_PROGRESS]: "in_progress",
  [BudgetStatus.CONFIRMED]: "completed",
  [BudgetStatus.COMPLETED]: "completed",
  [BudgetStatus.PAID]: "paid",
  [BudgetStatus.CANCELLED]: "cancelled",
  [BudgetStatus.OVER_BUDGET]: "in_progress",
};

/**
 * BudgetItem (форма / UI) -> DTO для createEventBudgetItem / updateEventBudgetItem (Events API).
 */
export function budgetItemToEventBudgetItem(item: BudgetItem): {
  title: string;
  description?: string;
  type: string;
  category: string;
  status: string;
  plannedAmount: number;
  actualAmount?: number;
  paidAmount?: number;
  dueDate?: Date;
  paymentDate?: Date;
} {
  const TYPE_OUT_MAP: Record<BudgetItemType, string> = {
    [BudgetItemType.INCOME]: "income",
    [BudgetItemType.EXPENSE]: "expense",
    [BudgetItemType.TRANSFER]: "transfer",
    [BudgetItemType.REFUND]: "refund",
  };
  const type = TYPE_OUT_MAP[item.type] ?? "expense";

  return {
    title: item.title,
    description: item.description ?? undefined,
    type,
    category: BUDGET_CATEGORY_TO_EVENT_CATEGORY[item.category] ?? "other",
    status: BUDGET_STATUS_TO_EVENT_STATUS[item.status] ?? "planned",
    plannedAmount: item.plannedAmount,
    actualAmount: item.actualAmount,
    paidAmount: item.actualAmount,
    dueDate:
      item.startDate instanceof Date
        ? item.startDate
        : new Date(item.startDate),
    paymentDate:
      item.endDate instanceof Date ? item.endDate : new Date(item.endDate),
  };
}

/** Поля формы создания/редактирования статьи (совместимы с BudgetItem по type/category/status). */
export interface EventBudgetItemFormDto {
  title: string;
  description?: string;
  type: BudgetItemType;
  category: BudgetCategory;
  status: BudgetStatus | BudgetItemStatus;
  plannedAmount: number;
  actualAmount?: number;
  startDate: Date | string;
  endDate: Date | string;
}

/**
 * Данные формы (BudgetFormState / EventBudgetItemFormDto) -> DTO для Events API.
 * Без приведения к BudgetItem.
 */
export function formStateToEventBudgetItemDto(state: EventBudgetItemFormDto): {
  title: string;
  description?: string;
  type: string;
  category: string;
  status: string;
  plannedAmount: number;
  actualAmount?: number;
  paidAmount?: number;
  dueDate?: Date;
  paymentDate?: Date;
} {
  const TYPE_OUT_MAP: Record<BudgetItemType, string> = {
    [BudgetItemType.INCOME]: "income",
    [BudgetItemType.EXPENSE]: "expense",
    [BudgetItemType.TRANSFER]: "transfer",
    [BudgetItemType.REFUND]: "refund",
  };
  const type = TYPE_OUT_MAP[state.type] ?? "expense";
  const startDate =
    state.startDate instanceof Date
      ? state.startDate
      : new Date(state.startDate);
  const endDate =
    state.endDate instanceof Date ? state.endDate : new Date(state.endDate);
  const result: {
    title: string;
    description?: string;
    type: string;
    category: string;
    status: string;
    plannedAmount: number;
    actualAmount?: number;
    paidAmount?: number;
    dueDate?: Date;
    paymentDate?: Date;
  } = {
    title: state.title,
    type,
    category: BUDGET_CATEGORY_TO_EVENT_CATEGORY[state.category] ?? "other",
    status:
      BUDGET_STATUS_TO_EVENT_STATUS[state.status as BudgetStatus] ??
      (state.status as string) ??
      "planned",
    plannedAmount: state.plannedAmount,
    dueDate: startDate,
    paymentDate: endDate,
  };
  if (state.description !== undefined && state.description !== "") {
    result.description = state.description;
  }
  if (state.actualAmount !== undefined) {
    result.actualAmount = state.actualAmount;
    result.paidAmount = state.actualAmount;
  }
  return result;
}

/**
 * Maps frontend BudgetCategory (events-unified) to backend BudgetItemCategory enum values.
 * Frontend uses short names ('venue', 'transport'); backend uses explicit names ('venue_rental', 'transportation').
 */
const FRONTEND_CATEGORY_TO_BACKEND: Record<string, string> = {
  venue: "venue_rental",
  venue_rental: "venue_rental",
  catering: "catering",
  speakers: "other",
  marketing: "marketing",
  equipment: "equipment",
  materials: "other",
  transport: "transportation",
  transportation: "transportation",
  accommodation: "transportation",
  entertainment: "other",
  staff: "staff",
  ticket_sales: "ticket_sales",
  sponsorship: "sponsorship",
  insurance: "insurance",
  other: "other",
};

/**
 * Maps frontend BudgetItemStatus (events-unified) to backend BudgetItemStatus enum values.
 * Frontend has 'ordered', 'paid', 'delivered' which the backend does not recognise.
 */
const FRONTEND_STATUS_TO_BACKEND: Record<string, string> = {
  planned: "planned",
  ordered: "in_progress",
  confirmed: "completed",
  paid: "completed",
  delivered: "completed",
  cancelled: "cancelled",
  approved: "approved",
  in_progress: "in_progress",
  completed: "completed",
  draft: "planned",
};

/**
 * Converts an EventBudgetItem (wizard form shape) into the payload accepted by
 * POST /events/:id/budget/items, normalising category and status to the backend enums.
 */
export function eventBudgetItemToCreateDto(
  item: EventBudgetItem,
): CreateBudgetItemPayload {
  const itemExt = item as EventBudgetItem & { type?: string };
  const rawCategory = String(item.category ?? "other").toLowerCase();
  const rawStatus = String(item.status ?? "planned").toLowerCase();

  const payload: CreateBudgetItemPayload = {
    title: item.name ?? item.title ?? "Статья",
    type: itemExt.type ?? "expense",
    category: FRONTEND_CATEGORY_TO_BACKEND[rawCategory] ?? "other",
    status: FRONTEND_STATUS_TO_BACKEND[rawStatus] ?? "planned",
    plannedAmount: item.plannedAmount ?? item.amount ?? 0,
  };

  const actualAmount = item.actualAmount ?? item.amount;
  if (actualAmount !== undefined) {
    payload.actualAmount = actualAmount;
  }
  if (item.description) {
    payload.description = item.description;
  }

  return payload;
}
